import { AnimatePresence, motion } from 'framer-motion';
import { useContext, useEffect, useState } from 'react';
import { Button, Form, Modal, Spinner } from 'react-bootstrap';
import { FaCheckCircle, FaPlus } from 'react-icons/fa';
import { FaUsersGear } from 'react-icons/fa6';
import { MdClose, MdViewColumn } from 'react-icons/md';
import { PiArrowBendUpLeft } from 'react-icons/pi';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { AuditObjectTypes } from '../../../_core/_model.ts';
import { AuthContext } from '../../../context/AuthContext.js';
import ComponentLayout from '../../Layouts/ComponentLayout.tsx';
import { AutoSelectDropdown } from '../../Tools/AutoSelectDropdown.tsx';
import { ColumnRestrictionGroup } from '../_core/_model.ts';
import {
    AddRestrictionColumn,
    GetColumnRestrictions,
    GetRestrictionsColumns,
    UpsertColumnRestrictions
} from '../_core/_request.ts';
import { useToast } from '../../../context/ToastContext.tsx';

interface Option {
    id: string;
    name: string;
}

const availableColumns: Option[] = [
    { id: "ReportName", name: "Report Name" },
    { id: "Created Date", name: "Created Date" },
    { id: "Owner", name: "Owner" },
    { id: "Access", name: "Access Level" },
];

const EditColumnRestriction = () => {
    const { user: loggedInUser } = useContext(AuthContext);
    const [searchParams] = useSearchParams();
    const [columns, setColumns] = useState<Option[]>([]);
    const [selectedGroup, setSelectedGroup] = useState<ColumnRestrictionGroup | null>(null);
    const [newUser, setNewUser] = useState("");
    const [newColumn, setNewColumn] = useState("");
    const { showToast } = useToast();

    // UI/UX States
    const [isSaving, setIsSaving] = useState(false);
    const [showAddColModal, setShowAddColModal] = useState(false);
    const [showSelectColModal, setShowSelectColModal] = useState(false);
    const [customColName, setCustomColName] = useState("");
    const [isAddingCol, setIsAddingCol] = useState(false);

    const navigate = useNavigate();

    const fetchColumns = () => {
        GetRestrictionsColumns().then((x) => {
            setColumns(x.data);
        }).catch(() => {
            setColumns(availableColumns);
        });
    };

    const initGroup = (id: number) => {
        fetchColumns();

        if (id === 0) {
            let g: ColumnRestrictionGroup = {
                byUser: loggedInUser.userID,
                columnRestrictionId: 0,
                name: "",
                users: [],
                restrictedColumns: []
            };
            setSelectedGroup({ ...g });
        } else {
            GetColumnRestrictions({
                columnRestrictionId: id, columns: null, users: null, name: null
            }).then((x) => {
                if (x.data.length > 0)
                    setSelectedGroup({ ...x.data[0] });
            }).catch((e) => {
                console.log("Error:" + e);
            });
        }
    };

    useEffect(() => {
        let tId = searchParams.get("id");
        if (tId != null) {
            let gId = parseInt(tId);
            initGroup(gId);
        }
    }, [searchParams]);

    const handleAddUser = () => {
        if (selectedGroup && newUser && !selectedGroup.users.includes(newUser)) {
            setSelectedGroup({
                ...selectedGroup,
                users: [...selectedGroup.users, newUser],
            });
            setNewUser("");
        }
    };

    const handleAddColumn = () => {
        if (selectedGroup && newColumn && !selectedGroup.restrictedColumns.includes(newColumn)) {
            setSelectedGroup({
                ...selectedGroup,
                restrictedColumns: [...selectedGroup.restrictedColumns, newColumn],
            });
            setNewColumn("");
            setShowSelectColModal(false);
        }
    };

    const handleCreateNewColumn = async () => {
        if (!customColName.trim()) return;
        setIsAddingCol(true);

        try {
            const res = await AddRestrictionColumn({ name: customColName.trim(), byUser: loggedInUser.userID });

            // Re-fetch list to ensure sync with backend
            await fetchColumns();

            // Auto-select the newly created column and open selection modal for confirmation
            if (res.data && res.data.id) {
                setNewColumn(res.data.id.toString());
                setShowSelectColModal(true);
            }

            setShowAddColModal(false);
            setCustomColName("");
        } catch (error) {
            console.error("Failed to create new column:", error);
        } finally {
            setIsAddingCol(false);
        }
    };

    const handleRemoveUser = (user: string) => {
        if (selectedGroup) {
            setSelectedGroup({
                ...selectedGroup,
                users: selectedGroup.users.filter((u) => u !== user),
            });
        }
    };

    const handleRemoveColumn = (col: string) => {
        if (selectedGroup) {
            setSelectedGroup({
                ...selectedGroup,
                restrictedColumns: selectedGroup.restrictedColumns.filter((c) => c !== col),
            });
        }
    };

    const handleSave = () => {
        if (!selectedGroup) return;
        setIsSaving(true);
        selectedGroup.byUser = loggedInUser.userID;
        UpsertColumnRestrictions(selectedGroup).then(() => {
            showToast({
                message: "Saved successfully",
                type: "success",
                duration: 5000
            });
            navigate("/columnRestrictions");
        }).catch((e) => {
            console.log("Error:" + e);
            showToast({
                message: "Failed to save column restriction",
                type: "warning",
                duration: 5000
            })
        }).finally(() => {
            setIsSaving(false);
        });
    };

    const canSave = () => {
        return selectedGroup && selectedGroup.name.trim().length > 0 && !isSaving;
    };

    // Helper to display real name instead of ID
    const getColumnDisplayName = (colId: string) => {
        const found = columns.find(c => c.id.toString() === colId);
        return found ? found.name : colId;
    };

    return (
        <>
            {selectedGroup != null && (
                <ComponentLayout
                    exButtons={
                        <button
                            disabled={!canSave()}
                            className={`j_u_sa_cl btn d-flex flex-row align-items-center gap-2 ${!canSave() ? "bg-secondary text-white opacity-50" : "btn-outline-primary"
                                }`}
                            onClick={handleSave}
                            style={{ transition: "all 0.2s ease" }}
                        >
                            {isSaving ? <Spinner animation="border" size="sm" /> : <FaCheckCircle />}
                            {isSaving ? "Saving..." : "Save & Close"}
                        </button>
                    }
                    instanceId={0}
                    objectType={AuditObjectTypes.ColumnRestrictions}
                    title={
                        <div className='d-flex flex-row align-items-center'>
                            {(selectedGroup.columnRestrictionId > 0) ?
                                <div className="d-flex flex-row align-items-center gap-1 u_edit-name">
                                    Edit Column Restriction: <b>{selectedGroup?.name}</b>
                                </div> :
                                <div className="d-flex flex-row align-items-center gap-1">New Column Restriction</div>
                            }
                            <span className='fs-3 px-2 mx-2 fw-normal' style={{ opacity: "0.2" }}>|</span>
                            <button className='btn px-0 j_u_bBtn' onClick={() => navigate("/columnRestrictions")}>
                                <div className='d-flex flex-row align-items-center gap-1 fw-500 t-user-back-btn'>
                                    <PiArrowBendUpLeft size={16} />Back to List
                                </div>
                            </button>
                        </div>
                    }
                    bodyHeight={400}
                    subHeader={undefined}
                    cBody={
                        <div>
                            <div className="card shadow-sm border-0 rounded-4">
                                <div className="card-body p-4">
                                    <Form.Group className="mb-4">
                                        <Form.Label className="fw-semibold">Group Name</Form.Label>
                                        <Form.Control
                                            type="text"
                                            placeholder="Enter a name for the group"
                                            value={selectedGroup.name}
                                            className="py-2"
                                            onChange={(e) =>
                                                setSelectedGroup({ ...selectedGroup, name: e.target.value })
                                            }
                                        />
                                        <Form.Text className="text-muted">
                                            Enter a descriptive name for this restriction group.
                                        </Form.Text>
                                    </Form.Group>

                                    <div className='w-100 d-flex flex-row align-items-stretch justify-content-between h-100'>
                                        <div className="" style={{ width: "100%" }}>
                                            <div className='d-flex flex-column'>
                                                <h6 className="fw-semibold mb-3">Restricted Columns</h6>

                                                <div className="w-100 d-flex flex-row align-items-center gap-2">
                                                    <Button
                                                        variant="danger"
                                                        onClick={() => setShowSelectColModal(true)}
                                                        className="px-4 py-2"
                                                    >
                                                        <div className='d-flex flex-row align-items-center gap-2 text-nowrap'>
                                                            <FaPlus /> Add Restriction
                                                        </div>
                                                    </Button>

                                                    <Button
                                                        variant="outline-secondary"
                                                        onClick={() => setShowAddColModal(true)}
                                                        className="px-3 py-2 border-2"
                                                    >
                                                        <div className='d-flex flex-row align-items-center gap-2 text-nowrap'>
                                                            <FaPlus /> New Column
                                                        </div>
                                                    </Button>
                                                </div>
                                                <Form.Text className="text-muted mt-2">
                                                    Columns added here will be hidden from users in this restriction group across all reports.
                                                </Form.Text>
                                            </div>

                                            <div className="my-4">
                                                {selectedGroup.restrictedColumns.length === 0 && (
                                                    <AnimatePresence>
                                                        <motion.div
                                                            className="d-flex flex-column align-items-center justify-content-center text-muted-theme p-5 bg-light"
                                                            initial={{ opacity: 0, scale: 0.95 }}
                                                            animate={{ opacity: 1, scale: 1 }}
                                                            transition={{ duration: 0.4, ease: "easeOut" }}
                                                            style={{
                                                                border: "2px dashed #dee2e6",
                                                                borderRadius: "12px",
                                                            }}
                                                        >
                                                            <FaUsersGear size={50} color="#adb5bd" className="mb-3" />
                                                            <h6 className="fw-semibold text-secondary">No Columns Restricted</h6>
                                                            <p className="text-center text-muted mb-0" style={{ maxWidth: 400 }}>
                                                                Click <strong>Add Restriction</strong> above to select and restrict a column.
                                                            </p>
                                                        </motion.div>
                                                    </AnimatePresence>
                                                )}

                                                <AnimatePresence>
                                                    {selectedGroup.restrictedColumns.map((col, indx) => (
                                                        <motion.div
                                                            key={col}
                                                            initial={{ opacity: 0, y: 10 }}
                                                            animate={{ opacity: 1, y: 0 }}
                                                            exit={{ opacity: 0, scale: 0.95 }}
                                                            className={`w-100 overflow-hidden d-flex justify-content-between align-items-center bg-white ${indx === 0 ? "border rounded-top" : "border-bottom border-start border-end"
                                                                } ${indx === selectedGroup.restrictedColumns.length - 1 ? "rounded-bottom" : ""}`}
                                                        >
                                                            <div className='d-flex flex-row align-items-center gap-3 px-3 py-2'>
                                                                <div className="p-2 bg-light rounded text-secondary">
                                                                    <MdViewColumn size={18} />
                                                                </div>
                                                                <span className="fw-medium text-dark">
                                                                    {getColumnDisplayName(col)}
                                                                </span>
                                                            </div>
                                                            <button
                                                                className="remove-btn"
                                                                title="Remove Restriction"
                                                                onClick={() => handleRemoveColumn(col)}
                                                            >
                                                                <MdClose size={18} />
                                                            </button>
                                                        </motion.div>
                                                    ))}
                                                </AnimatePresence>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <style>{`
                                .remove-btn {
                                    background-color: transparent;
                                    border: none;
                                    color: #dc3545;
                                    padding: 0 20px;
                                    height: 52px;
                                    display: flex;
                                    align-items: center;
                                    justify-content: center;
                                    cursor: pointer;
                                    border-left: 1px solid #dee2e6;
                                    transition: all 0.2s ease;
                                }
                                .remove-btn:hover {
                                    background-color: #dc3545;
                                    color: white;
                                }
                            `}</style>
                        </div>
                    }
                />
            )}

            {/* Select Column to Add Restriction Modal */}
            <Modal
                show={showSelectColModal}
                onHide={() => {
                    setShowSelectColModal(false);
                    setNewColumn("");
                }}
                centered
                backdrop="static"
            >
                <Modal.Header closeButton className="border-0 pb-0">
                    <Modal.Title className="fs-5 fw-bold">Select Column to Restrict</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <Form.Group>
                        <Form.Label className="text-secondary small fw-semibold">AVAILABLE COLUMNS</Form.Label>
                        <AutoSelectDropdown
                            disabled={false}
                            placeholder="Select a Column"
                            emptyString="No available columns left to restrict"
                            options={columns
                                .filter(c => !selectedGroup?.restrictedColumns.includes(c.id.toString()))
                                .map((u) => ({ id: u.id.toString(), name: u.name }))
                            }
                            initSelect={newColumn}
                            onSelect={(e) => setNewColumn(e)}
                        />
                        <Form.Text className="text-muted d-block mt-2">
                            Select the column you wish to add to the restriction group.
                        </Form.Text>
                    </Form.Group>
                </Modal.Body>
                <Modal.Footer className="border-0 pt-0">
                    <Button
                        variant="light"
                        className="px-4"
                        onClick={() => {
                            setShowSelectColModal(false);
                            setNewColumn("");
                        }}
                    >
                        Cancel
                    </Button>
                    <Button
                        variant="danger"
                        className="px-4 d-flex align-items-center gap-2"
                        onClick={handleAddColumn}
                        disabled={!newColumn}
                    >
                        <FaCheckCircle /> Include Column
                    </Button>
                </Modal.Footer>
            </Modal>

            {/* Create New Column Modal */}
            <Modal
                show={showAddColModal}
                onHide={() => !isAddingCol && setShowAddColModal(false)}
                centered
                backdrop="static"
            >
                <Modal.Header closeButton={!isAddingCol} className="border-0 pb-0">
                    <Modal.Title className="fs-5 fw-bold">Create New Column</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <Form.Group>
                        <Form.Label className="text-secondary small fw-semibold">COLUMN NAME</Form.Label>
                        <Form.Control
                            type="text"
                            placeholder="e.g., Financial Data, Department ID..."
                            value={customColName}
                            onChange={(e) => setCustomColName(e.target.value)}
                            disabled={isAddingCol}
                            autoFocus
                            className="py-2"
                        />
                        <Form.Text className="text-muted">
                            This column will become available for restrictions globally.
                        </Form.Text>
                    </Form.Group>
                </Modal.Body>
                <Modal.Footer className="border-0 pt-0">
                    <Button
                        variant="light"
                        className="px-4"
                        onClick={() => setShowAddColModal(false)}
                        disabled={isAddingCol}
                    >
                        Cancel
                    </Button>
                    <Button
                        variant="primary"
                        className="px-4 d-flex align-items-center gap-2"
                        onClick={handleCreateNewColumn}
                        disabled={!customColName.trim() || isAddingCol}
                    >
                        {isAddingCol ? <Spinner animation="border" size="sm" /> : <FaCheckCircle />}
                        {isAddingCol ? "Creating..." : "Create Column"}
                    </Button>
                </Modal.Footer>
            </Modal>
        </>
    );
};

export default EditColumnRestriction;